import {
  Input,
  OnInit,
  Component,
  ViewChild,
  AfterViewInit,
} from '@angular/core';

import { Session } from '@services/session';

import { getSpendProfileTimeLimitText } from '@app/admin/fleet-card/shared/utils';

import {
  ISpendCategory,
  IChildSpendCategory,
  ICardHolderSpendProfile,
} from '@app/admin/fleet-card/shared/spend-controls/spendcontrols.interface';
import { ISpendProfileTooltip } from '@app/admin/fleet-card/card-management-v2/card-management.interface';

import { SPEND_SUB_CATEGORY_LIMITS_MAP } from '@app/admin/fleet-card/shared/spend-controls/main.constants';

@Component({
  selector: 'spend-control-tooltip-v4',
  templateUrl: './spend-control-tooltip-v4.component.html',
  styleUrls: ['./spend-control-tooltip-v4.component.scss'],
})
export class SpendControlTooltipV4Component implements OnInit, AfterViewInit {
  @Input() public spendProfile: ICardHolderSpendProfile;
  @Input() public spendCategoriesDetailsList: ISpendCategory[];

  @ViewChild('granularSpendControlTooltip') granularSpendControlTooltip;

  public timeLimitText: string = '';
  public shouldShowCashAdvance: boolean = false;
  public isCardManagementManageEnabled: boolean;
  public removedSubCategoryLimitsCount: number = 0;
  public isTooltipMaxHeightReached: boolean = true;
  public areAllSpendCategoriesAllowed: boolean = false;
  public spendCategoriesTooltipData: ISpendProfileTooltip[] = [];
  public allowedSpendCategoriesNamesWithNoSetLimits: { [key: string]: string[] } = {};

  public readonly MAX_HEIGHT_PX: number = 450;
  public readonly SPEND_SUBCATEGORY_LIMITS_MAP: string[][] = SPEND_SUB_CATEGORY_LIMITS_MAP;

  constructor(public session: Session) { }

  ngOnInit() {
    if (this.spendProfile) {
      this.timeLimitText = getSpendProfileTimeLimitText(this.spendProfile);
    }

    this.isCardManagementManageEnabled = this.session.isCardManagementManageEnabled();
    this.areAllSpendCategoriesAllowed = this.spendProfile.spendCategories === null;
    this.shouldShowCashAdvance = this.spendProfile.cashAdvanceEnabled && this.spendProfile.atmWithdrawalEnabled &&
      (this.spendProfile.dailyWithdrawalLimitInCents > 0 || this.spendProfile.weeklyWithdrawalLimitInCents > 0);

    if (!this.areAllSpendCategoriesAllowed) {
      this.setCategorySpendLimits();
    }
  }

  ngAfterViewInit() {
    // we need this timeout as the tooltip height appears 0
    // when hovered over the element the second time
    setTimeout(() => {
      if (this.isMaxHeightReached()) this.removeCategoryLimits();
    }, 0);
  }

  public getFuelOnlyCategoryText(categories: string[]): string {
    const updatedCategories = categories.map(category => {
      if (category.toLowerCase().includes('in-store')) {
        return category.replace('includes', '<b>excludes</b>');
      }

      return category;
    });

    return updatedCategories.join(', ');
  }

  public isAnySpendLimitSet(): boolean {
    return !!this.spendProfile.dailySpendLimit ||
      !!this.spendProfile.weeklySpendLimit ||
      !!this.spendProfile.monthlySpendLimit ||
      !!this.spendProfile.billingCycleSpendLimit ||
      !!this.spendProfile.transactionLimit;
  }

  private setCategorySpendLimits(): void {
    const allowedSpendCategories: string[] = this.spendProfile?.spendCategories?.flat() || [];

    this.spendCategoriesTooltipData =
      this.spendCategoriesDetailsList.map((spendCategory: ISpendCategory): ISpendProfileTooltip => {
        const subCategories: IChildSpendCategory[] = spendCategory.spendCategories.filter(
          (subCategory: IChildSpendCategory): boolean => {
            const isSubCategoryAllowed: boolean = allowedSpendCategories.includes(subCategory.categoryName);
            const areSpendLimitsSet: boolean = !!this.spendProfile.categorySpecificSpendLimits[subCategory.id];

            if (isSubCategoryAllowed && !areSpendLimitsSet) {
              if (this.allowedSpendCategoriesNamesWithNoSetLimits[spendCategory.id]) {
                this.allowedSpendCategoriesNamesWithNoSetLimits[spendCategory.id].push(subCategory.categoryName);
              } else {
                this.allowedSpendCategoriesNamesWithNoSetLimits[spendCategory.id] = [subCategory.categoryName];
              }
            }

            return isSubCategoryAllowed && areSpendLimitsSet;
          },
        );

        return {
          categoryId: spendCategory.id,
          categoryName: spendCategory.categoryName,
          subCategories,
        };
      });
  }

  private removeCategoryLimits(): void {
    // 1. Remove a category from the bottom of the category limits section
    // 2. Keep count of the number of sub category limits of the removed section
    // 3. If the item removed is of "Allowed" category, then add the count of all those sub categories to the removed count
    // 4. If the height is still greater than the max height, then repeat from step 1
    // 5. If the height is less than the max height, then stop and add back that section

    let isTooltipOfDesiredHeight: boolean = false;
    const tooltip: HTMLDivElement = this.granularSpendControlTooltip.nativeElement;
    const allCategoryLimits: any = tooltip.querySelectorAll('.category-limit');
    const totalCategoryLimits: number = allCategoryLimits.length;

    for (let categoryIndex = totalCategoryLimits - 1; categoryIndex >= 0; categoryIndex--) {
      if (isTooltipOfDesiredHeight) break;

      const categoryToBeRemoved: any = allCategoryLimits[categoryIndex];
      const subCategoriesListSelector: string = '.subcategories-list';
      const allListItems: any = categoryToBeRemoved.querySelectorAll(subCategoriesListSelector);

      for (let subCategoryIndex = allListItems.length - 1; subCategoryIndex >= 0; subCategoryIndex--) {
        const subCategoryListItemToBeRemoved: HTMLUListElement = allListItems[subCategoryIndex] as HTMLUListElement;

        if ((this.getTooltipHeight() - subCategoryListItemToBeRemoved.scrollHeight) > this.MAX_HEIGHT_PX) {
          if (subCategoryListItemToBeRemoved.classList.contains('subcategories-without-limits')) {
            const categoryId: string = subCategoryListItemToBeRemoved.getAttribute('data-spendCategoryId');
            this.removedSubCategoryLimitsCount += this.allowedSpendCategoriesNamesWithNoSetLimits[categoryId].length;
          } else {
            this.removedSubCategoryLimitsCount += 1;
          }
          subCategoryListItemToBeRemoved.remove();
        } else {
          isTooltipOfDesiredHeight = true;
          break;
        }
      }

      // remove the category, which contains the title, if all subcategories are removed
      if (categoryToBeRemoved.querySelectorAll(subCategoriesListSelector).length === 0) {
        categoryToBeRemoved.remove();
        isTooltipOfDesiredHeight = this.getTooltipHeight() <= this.MAX_HEIGHT_PX;
      }
    }

    // to recenter the tooltip to the item
    window.dispatchEvent(new Event('resize'));
  }

  private getTooltipHeight(): number {
    const tooltip: HTMLElement = this.granularSpendControlTooltip.nativeElement as HTMLElement;

    // 12px padding on top and bottom
    const tooltipHeight: number = tooltip ? tooltip.scrollHeight + 24 : 0;
    return tooltipHeight;
  }

  private isMaxHeightReached(): boolean {
    return this.getTooltipHeight() >= this.MAX_HEIGHT_PX;
  }
}